import Avatar from '@mui/material/Avatar'
import { lighten } from '@mui/system'
import { AvatarSize, getAvatarIconSize } from 'components/core/avatar/AvatarSizes'
import { avatarStyles } from 'components/core/avatar/styles'
import { C1Icon } from 'components/core/icons/C1Icon'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { BadgeCheck, Blocks, FileCode, Key, LayoutGrid, Lock, LucideIcon, Tag, Users, Workflow } from 'lucide-react'
import React, { ReactNode } from 'react'
import { IntlShape, useIntl } from 'react-intl'

type ResourceTypeInfo = {
    altText: string
    icon: LucideIcon
    color: string
}

export const getResourceTypeInfo = (intl: IntlShape, type?: string): ResourceTypeInfo => {
    const custom = {
        altText: intl.formatMessage({
            id: 'entitlement_custom',
            defaultMessage: 'Custom entitlement',
        }),
        icon: LayoutGrid,
        color: 'icons.gold',
    }

    if (!type) return custom

    switch (type.toLowerCase()) {
        case 'org':
            return {
                altText: intl.formatMessage({
                    id: 'entitlement_org',
                    defaultMessage: 'Organization entitlement',
                }),
                icon: Users,
                color: 'icons.aqua',
            }
        case 'group':
            return {
                altText: intl.formatMessage({
                    id: 'entitlement_groups',
                    defaultMessage: 'Group entitlement',
                }),
                icon: Users,
                color: 'icons.eggplant',
            }
        case 'credential':
            return {
                altText: intl.formatMessage({
                    id: 'entitlement_creds',
                    defaultMessage: 'Credential entitlement',
                }),
                icon: Key,
                color: 'icons.chartreuse',
            }
        case 'vault':
            return {
                altText: intl.formatMessage({
                    id: 'entitlement_vault',
                    defaultMessage: 'Vault entitlement',
                }),
                icon: Lock,
                color: 'icons.vermillion',
            }
        case 'repository':
            return {
                altText: intl.formatMessage({
                    id: 'entitlement_repos',
                    defaultMessage: 'Repository entitlement',
                }),
                icon: FileCode,
                color: 'icons.turquoise',
            }
        case 'app':
            return {
                altText: intl.formatMessage({
                    id: 'entitlement_app',
                    defaultMessage: 'App entitlement',
                }),
                icon: Blocks,
                color: 'icons.violet',
            }
        case 'role':
            return {
                altText: intl.formatMessage({
                    id: 'entitlement_role',
                    defaultMessage: 'Role entitlement',
                }),
                icon: BadgeCheck,
                color: 'icons.fuschia',
            }
        case 'catalog':
            return {
                altText: intl.formatMessage({
                    id: 'entitlement_catalog',
                    defaultMessage: 'Catalog entitlement',
                }),
                icon: Users,
                color: 'icons.teal',
            }
        case 'service account key':
        case 'api token':
        case 'api key':
            return {
                altText: intl.formatMessage({
                    id: 'resource_api_key',
                    defaultMessage: 'API Key',
                }),
                icon: Workflow,
                color: 'icons.chartreuse',
            }
        case 'profile type':
            return {
                altText: intl.formatMessage({
                    id: 'entitlement_profile_type',
                    defaultMessage: 'Profile type entitlement',
                }),
                icon: Tag,
                color: 'icons.mauve',
            }
        default:
            return custom
    }
}

// Convenience functions that destructure from the main function
export const getResourceTypeAvatarProps = (
    intl: IntlShape,
    type?: string,
): { altText: string; icon: LucideIcon } => {
    const { altText, icon } = getResourceTypeInfo(intl, type)
    return { altText, icon }
}

export const getResourceTypeColor = (intl: IntlShape, type?: string): string => {
    const { color } = getResourceTypeInfo(intl, type)
    return color
}

export const ResourceTypeAvatar = ({
    resourceTypeName,
    size = 'md',
}: {
    resourceTypeName?: string
    size?: AvatarSize
}): React.ReactElement => {
    const intl = useIntl()
    const { altText, icon, color } = getResourceTypeInfo(intl, resourceTypeName?.toLowerCase())
    const iconSize = getAvatarIconSize(size)

    return (
        <Avatar
            alt={altText}
            sx={sxc(
                avatarStyles.avatar,
                avatarStyles.resourceTypeAvatar,
                size && avatarStyles[size],
                {
                    borderColor: (t) => {
                        const [path1, path2] = color.split('.')

                        return lighten(t.palette[path1][path2], 0.6)
                    },
                },
            )}
        >
            <C1Icon icon={icon} sx={{ color, fontSize: iconSize }} />
        </Avatar>
    )
}

type ResourceTypeBadgeProps = {
    resourceType: string
    size?: AvatarSize
    /** When true, image is decorative with empty alt. Set false when used standalone without visible text. */
    decorative?: boolean
}

export const ResourceTypeBadge = ({ resourceType, size = 'xs', decorative = false }: ResourceTypeBadgeProps): ReactNode => {
    const intl = useIntl()
    const { altText, icon, color } = getResourceTypeInfo(intl, resourceType.toLowerCase())
    const iconSize = getAvatarIconSize(size)
    const alt = decorative ? '' : altText

    return <Avatar
        alt={alt}
        sx={sxc(
            avatarStyles.avatar,
            avatarStyles.resourceTypeAvatar,
            size && avatarStyles[size],
            { backgroundColor: color, borderColor: 'transparent' },
        )}
    >
        <C1Icon icon={icon} sx={{ color: 'common.white', fontSize: iconSize }} aria-hidden={decorative} />
    </Avatar>
}
